import { CapTableHome } from "components/CapTable/CapTableHome";
import { DashboardLayout } from "components/DashboardLayout";
import { Header } from "components/DesignSystem/Header/Header";
import Loader from "components/DesignSystem/Loader/loader";
import Tab from "components/DesignSystem/Tab/Tab";
import { usePageBreadcrumbValue } from "hooks/usePageBreadcrumbValue";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useEffect, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { useQuery } from "hooks/useQuery";
import EditJurisdiction from "./EntityFormEngine/EditJurisdiction";
import { useGetEntityDataAttributesQuery } from "store/apis/entity";
import { useGetGroupDataQuery } from "store/apis/group";
import { PeopleTable } from "./People/PeopleTable";
import { PeopleOwnershipHeader } from "./People/PeopleOwnershipHeader";
import { AddPersonModal } from "./People/AddPersonModal";
import EntityFormEngine from "./EntityFormEngine/EntityFormEngine";
import { EntityProfile } from "v2/pages/EntityProfile/EntityProfile";
import { EntityTopBar } from "./EntityTopBar/EntityTopBar";
import { ShareHolders } from "./Shareholders/Shareholders";
import { SensitiveData } from "components/SensitiveData/SensitiveData";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { useIsGroupV2Platform } from "hooks/useIsGroupV2Platform";

const EntityDetail = ({ parentUrl }: { parentUrl?: string }) => {
  const { path, url } = useRouteMatch();
  const { pathname } = useLocation();
  const query = useQuery();
  const { groupId } = useParams<{ groupId?: string }>();
  const { entityId } = useParams<{ entityId: string }>();
  const { isAdmin, isCustomer } = useRoleBasedView();
  const { uuid: currentGroupId } = useCurrentGroupContext();
  const isV2Platform = useIsGroupV2Platform();

  const { data: currentEntityDetails, ...currentEntityDetailsAPIStates } =
    useGetEntityDataAttributesQuery(
      {
        groupId: currentGroupId!,
        entityId,
      },
      {
        skip: !currentGroupId || !entityId,
      }
    );

  const { data: adminGroup, ...groupDataAPIStates } = useGetGroupDataQuery(
    {
      groupId: groupId!,
    },
    {
      skip: !groupId,
    }
  );

  const { dispatch } = useCurrentGroupContext();
  const editJurisdictionModal = useModal(
    query.get("openEditJurisdiction") === "true"
  );
  const addPersonModal = useModal();
  const [restrictAddToPersonOnly, setRestrictAddToPersonOnly] = useState(false);
  const openAddPersonModal = () => {
    setRestrictAddToPersonOnly(false);
    addPersonModal.open();
  };
  const openAddOfficerModal = () => {
    setRestrictAddToPersonOnly(true);
    addPersonModal.open();
  };

  useEffect(() => {
    if (adminGroup) {
      dispatch({ type: "SET_GROUP", payload: adminGroup });
    }
  }, [adminGroup]);

  const { value: entityName = "" } =
    currentEntityDetails?.find(({ name }) => name === "entity_name") || {};

  const name = entityName as string;

  usePageTitle(name || `${"Entity"}`);

  const pageBreadcrumb = usePageBreadcrumbValue();

  const showBreadcrumbs = !pageBreadcrumb && !isCustomer;

  const requiredQueries = isAdmin
    ? [currentEntityDetailsAPIStates, groupDataAPIStates]
    : [currentEntityDetailsAPIStates];

  const isLoading = requiredQueries.some(({ isLoading }) => isLoading);
  const isError = requiredQueries.some(({ isError }) => isError);
  const isSuccess = requiredQueries.every(({ isSuccess }) => isSuccess);

  const isWaitingToStart = requiredQueries.some(
    ({ isUninitialized }) => isUninitialized
  );

  const hasNoEntityDetails = currentEntityDetails?.length === 0;
  const isEmpty = isAdmin
    ? hasNoEntityDetails || !adminGroup
    : hasNoEntityDetails;

  if (isLoading || isWaitingToStart) {
    return (
      <div className="t-w-full">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={isEmpty}
        isError={isError}
      >
        <Async.Empty>
          <></>
        </Async.Empty>
        <Async.ErrorHandler>
          <div className="t-flex t-min-h-[60dvh] t-items-center t-justify-center t-text-body t-text-text-30">
            We could not load this entity.
          </div>
        </Async.ErrorHandler>
        <Async.Success>
          <DashboardLayout
            contentProps={{ className: "t-mb-6" }}
            header={
              showBreadcrumbs ? (
                <Header
                  v2
                  breadcrumbs={[
                    { name: "Entities", link: parentUrl },
                    { name },
                  ]}
                />
              ) : null
            }
          >
            <div className="t-mx-auto t-flex t-w-full t-max-w-[1280px] t-flex-col">
              <EntityTopBar />

              {!isCustomer && (
                <div>
                  <Tab.Root defaultValue={pathname} value={pathname}>
                    <Tab.List>
                      {isV2Platform && (
                        <Tab.NavTrigger replace exact value={url}>
                          Records & Setup
                        </Tab.NavTrigger>
                      )}
                      <Tab.NavTrigger
                        replace
                        exact
                        value={isV2Platform ? `${url}/profile` : url}
                      >
                        Entity Details
                      </Tab.NavTrigger>
                      <Tab.NavTrigger exact value={`${url}/people-ownership`}>
                        People & ownership
                      </Tab.NavTrigger>
                      <Tab.NavTrigger exact value={`${url}/sensitive-data`}>
                        Sensitive Data
                      </Tab.NavTrigger>
                    </Tab.List>
                  </Tab.Root>
                </div>
              )}

              <div>
                <Switch>
                  <Route exact path={path}>
                    {isV2Platform ? <EntityProfile /> : <EntityFormEngine />}
                  </Route>
                  {isV2Platform && (
                    <Route exact path={`${path}/profile`}>
                      <EntityFormEngine />
                    </Route>
                  )}
                  <Redirect
                    exact
                    from={`${path}/cap-table`}
                    to={`${path}/people-ownership`}
                  />
                  <Redirect
                    exact
                    from={`${path}/directors-officers`}
                    to={`${path}/people-ownership`}
                  />
                  <Redirect
                    exact
                    from={`${path}/shareholders`}
                    to={`${path}/people-ownership`}
                  />
                  <Route exact path={`${path}/people-ownership`}>
                    <div className="t-flex t-flex-col t-gap-5">
                      <PeopleOwnershipHeader onAdd={openAddPersonModal} />
                      <div className="t-flex t-flex-col t-gap-5">
                        <PeopleTable onAddPerson={openAddOfficerModal} />
                        <ShareHolders onAddPerson={openAddPersonModal} />
                        <CapTableHome />
                      </div>
                    </div>
                    {addPersonModal.isOpen && (
                      <AddPersonModal
                        {...addPersonModal}
                        restrictToPersonOnly={restrictAddToPersonOnly}
                      />
                    )}
                  </Route>
                  <Route path={`${path}/sensitive-data`}>
                    <SensitiveData />
                  </Route>
                </Switch>
              </div>
            </div>
          </DashboardLayout>
        </Async.Success>
      </Async.Root>
      {query.get("stateId") && (
        <EditJurisdiction
          isOpen={editJurisdictionModal.isOpen}
          close={editJurisdictionModal.close}
          stateId={query.get("stateId")!}
          entityId={entityId}
        />
      )}
    </>
  );
};

export default EntityDetail;
